import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { z } from 'zod';
import { usePasswordRecovery } from '../hooks/auth-actions.js';
import { describeError, mergeFormClasses } from './shared.js';
const emailSchema = z.string().trim().min(1, 'Enter your email.').email('Enter a valid email.');
const completeSchema = z.object({
    code: z.string().trim().min(4, 'Enter the recovery code.'),
    newPassword: z.string().min(8, 'Use at least 8 characters.'),
    confirmPassword: z.string().min(1, 'Re-enter the new password.'),
});
/**
 * Two-step password recovery: first the user requests a code via email, then
 * supplies the code and a new password. On success the API returns a fresh
 * session, so the component completes login as well.
 */
export function PasswordRecoveryForm({ onSuccess, classNames, errorMessages, }) {
    const classes = mergeFormClasses(classNames);
    const { initiate, complete } = usePasswordRecovery();
    const [email, setEmail] = React.useState('');
    const [flowId, setFlowId] = React.useState(null);
    const [code, setCode] = React.useState('');
    const [newPassword, setNewPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [validationError, setValidationError] = React.useState(null);
    const error = describeError(initiate.error ?? complete.error, errorMessages);
    const handleSendCode = async (event) => {
        event.preventDefault();
        const parsed = emailSchema.safeParse(email);
        if (!parsed.success) {
            setValidationError(parsed.error.issues[0]?.message ?? 'Enter a valid email.');
            return;
        }
        setValidationError(null);
        try {
            const result = await initiate.run({ email: parsed.data });
            setFlowId(result.flowId);
        }
        catch {
            // Error captured by hook.
        }
    };
    const handleSubmit = async (event) => {
        event.preventDefault();
        if (flowId === null)
            return;
        const parsed = completeSchema.safeParse({ code, newPassword, confirmPassword });
        if (!parsed.success) {
            setValidationError(parsed.error.issues[0]?.message ?? 'Enter all fields.');
            return;
        }
        if (parsed.data.newPassword !== parsed.data.confirmPassword) {
            setValidationError('Passwords do not match.');
            return;
        }
        setValidationError(null);
        try {
            const result = await complete.run({
                flowId,
                code: parsed.data.code,
                newPassword: parsed.data.newPassword,
            });
            onSuccess(result);
        }
        catch {
            // Error captured by hook.
        }
    };
    if (flowId === null) {
        return (_jsxs("form", { className: classes.form, onSubmit: handleSendCode, noValidate: true, children: [_jsx("p", { className: classes.hint, children: "Enter the email on your account and we'll send a recovery code." }), _jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-recover-email", children: "Email" }), _jsx("input", { id: "auth-ui-recover-email", className: classes.input, type: "email", autoComplete: "email", required: true, value: email, onChange: (event) => setEmail(event.target.value) }), validationError !== null ? (_jsx("p", { className: classes.error, role: "alert", children: validationError })) : null] }), error !== null ? (_jsx("p", { className: classes.error, role: "alert", children: error })) : null, _jsx("button", { type: "submit", className: classes.button, disabled: initiate.loading, children: initiate.loading ? 'Sending…' : 'Send recovery code' })] }));
    }
    return (_jsxs("form", { className: classes.form, onSubmit: handleSubmit, noValidate: true, children: [_jsxs("p", { className: classes.hint, children: ["Enter the code sent to ", email, " and choose a new password."] }), _jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-recover-code", children: "Recovery code" }), _jsx("input", { id: "auth-ui-recover-code", className: classes.input, inputMode: "numeric", autoComplete: "one-time-code", required: true, value: code, onChange: (event) => setCode(event.target.value) })] }), _jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-recover-new-password", children: "New password" }), _jsx("input", { id: "auth-ui-recover-new-password", className: classes.input, type: "password", autoComplete: "new-password", required: true, value: newPassword, onChange: (event) => setNewPassword(event.target.value) })] }), _jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-recover-confirm-password", children: "Confirm new password" }), _jsx("input", { id: "auth-ui-recover-confirm-password", className: classes.input, type: "password", autoComplete: "new-password", required: true, value: confirmPassword, onChange: (event) => setConfirmPassword(event.target.value) })] }), validationError !== null ? (_jsx("p", { className: classes.error, role: "alert", children: validationError })) : null, error !== null ? (_jsx("p", { className: classes.error, role: "alert", children: error })) : null, _jsx("button", { type: "submit", className: classes.button, disabled: complete.loading, children: complete.loading ? 'Updating…' : 'Set new password' }), _jsx("button", { type: "button", className: classes.link, onClick: () => {
                    setFlowId(null);
                    setCode('');
                    setNewPassword('');
                    setConfirmPassword('');
                }, children: "Use a different email" })] }));
}
//# sourceMappingURL=PasswordRecoveryForm.js.map